/* eslint-disable max-len */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { capitalizeAll, SlideOutComp, SlideOutCompProps } from '@moderepo/mode-app-components';
import { DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, setIsEditingForm, showDialog, showErrorNotification,
    showSuccessNotification, SlideOutPanelOptions } from '@moderepo/mode-ui-state';
import { ApiError, EntityClass } from '@moderepo/mode-apis';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { userAppDataContext, updateEntityClass, createEntityClass, deleteEntityClass } from '@moderepo/mode-data-state-user-app';
import { ProjectTypeHandlersManager, ProjectTypeSlideOutCompHandler } from '../../utils/ritzExtensionUtils';
import { getEntityClassKeyPrefix, getEntityClassNameKey, getEntityClassDescriptionKey, getAttributeSchemaKeyPrefix,
    getAttributeSchemaLabelKey, getAttributeSchemaDescriptionKey, getAttributeSchemaOptionKeyPrefix, getAttributeSchemaOptionLabelKey,
    getMetricsDefinitionKeyPrefix, getMetricsDefinitionDisplayNameKey, getNumericMetricsDefinitionKeyPrefix, getNumericMetricDisplayNameKey,
    getNumericMetricUnitKey, getNumericMetricEvaluatorDisplayNameKey, getTagMetricsDefinitionKeyPrefix, getTagMetricDisplayNameKey,
    getEnumDefinitionKeyPrefix, getEnumDefinitionNameKey } from './entityClassesUtils';
import { EntityClassInfo, EntityClassInfoProps } from './EntityClassInfo';
import { useRitzTranslation } from '../../utils/customHooks';
import { isObjectLiteral } from '../../utils/utils';
import { getDateTimeDisplay } from '../containerUtils';

/**
 * These are the list of components that can be displayed in the SlideOut panel.
 */
export enum EntitySlideOutPanelCompType {
    ENTITY_CLASS_PREVIEW = 'entity_class_preview',
}

/**
 * The options required for showing preview of a Entity Class in the Slide Out panel
 */
export interface EntityClassPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: EntitySlideOutPanelCompType.ENTITY_CLASS_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;

            // entityClass is undefined when the user wants to CREATE a new entity class
            readonly entityClass?: EntityClass | undefined;
        }
    }
}

/**
 * Type guard check to make sure the provided options is the options for EntityPreviewCompOptions
 */
export const isEntityClassPreviewCompOptions = (options: unknown): options is EntityClassPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === EntitySlideOutPanelCompType.ENTITY_CLASS_PREVIEW;
};

/**
 * When we register the slide out panels with the ProjectTypeHandlersManager, we need to give it a ProjectTypeSlideOutCompHandler. The
 * ProjectTypeHandlersManager doesn't know how to create slide out panels therefore it depends on the ProjectTypeSlideOutCompHandler. Whenever
 * the ProjectTypeHandlersManager needs to create slide out panel for a given type, it will call the handler to create it. This is the
 * implementation of the ProjectTypeSlideOutCompHandler interface which has only 1 function, "getSlideOutComponent".
 */
const EntityClassSlideOutComponentsHandler: ProjectTypeSlideOutCompHandler = {

    getSlideOutComponent: (options: SlideOutPanelOptions): React.ReactElement<SlideOutCompProps> | undefined => {

        if (isEntityClassPreviewCompOptions(options)) {
            return (
                <EntityClassPreviewContainer
                    projectId={options.compProps.data.projectId}
                    entityClass={options.compProps.data.entityClass}
                />
            );
        }
        return undefined;
    },
};

/**
 * Register the slide out panel types and the handler that handle those types.
 */
ProjectTypeHandlersManager.getInstance().registerSlideOutComponentHandler(
    EntitySlideOutPanelCompType.ENTITY_CLASS_PREVIEW, EntityClassSlideOutComponentsHandler,
);

const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            '& .slide-out-comp-container': {
                minWidth: 1000,
            },
        },

        entityClassPanel: {
            height: '100%',
        },

        entityClassContainer: {
            height: '100%',
            width : '100%',
        },
        entityClassContainerWrapper: {
            height: '100%',
            width : '100%',
        },
        entityClassTabsContainer: {
            height        : '100%',
            width         : '100%',
            display       : 'flex',
            flexDirection : 'column',
            justifyContent: 'center',
        },
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.delete': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface EntityClassPreviewContainerProps {
    readonly projectId: number;
    readonly entityClass?: EntityClass | undefined;
}

const replaceEntityClassAttributeSchemaAttributeValuesWithTranslationKeys = (
    keyPrefix: string,
    attributeSchema: EntityClass['attributeSchema'],
): EntityClass['attributeSchema'] => {
    if (attributeSchema) {
        return attributeSchema.map((schema) => {
            const attrKeyPrefix = getAttributeSchemaKeyPrefix(keyPrefix, schema.key);

            return {
                ...schema,
                label      : getAttributeSchemaLabelKey(attrKeyPrefix),
                description: getAttributeSchemaDescriptionKey(attrKeyPrefix),
                options    : schema.options ? schema.options.map((option) => {
                    const optionKeyPrefix = getAttributeSchemaOptionKeyPrefix(attrKeyPrefix, option.value.toString());
                    return {
                        ...option,
                        label: getAttributeSchemaOptionLabelKey(optionKeyPrefix),
                    };
                }) : undefined,
            };
        });
    }
    return undefined;
};

const replaceEntityClassMetricsDefinitionsAttributeValuesWithTranslationKeys = (
    keyPrefix: string,
    metricsDefinitions: EntityClass['metricsDefinitions'],
): EntityClass['metricsDefinitions'] => {
    if (metricsDefinitions) {
        return metricsDefinitions.map((metricsDefinition) => {
            const metricsDefKeyPrefix = getMetricsDefinitionKeyPrefix(keyPrefix, metricsDefinition.metricsDefinitionId);

            return {
                ...metricsDefinition,
                displayName   : getMetricsDefinitionDisplayNameKey(metricsDefKeyPrefix),
                numericMetrics: metricsDefinition.numericMetrics?.map((metric) => {
                    const metricKeyPrefix = getNumericMetricsDefinitionKeyPrefix(metricsDefKeyPrefix, metric.name);
                    return {
                        ...metric,
                        displayName: getNumericMetricDisplayNameKey(metricKeyPrefix),
                        unit       : metric.unit ? getNumericMetricUnitKey(metricKeyPrefix) : undefined,
                        evaluator  : metric.evaluator ? {
                            ...metric.evaluator,
                            displayName: getNumericMetricEvaluatorDisplayNameKey(metricKeyPrefix),
                        } : undefined,
                    };
                }),
                tagMetrics: metricsDefinition.tagMetrics?.map((metric) => {
                    const metricKeyPrefix = getTagMetricsDefinitionKeyPrefix(metricsDefKeyPrefix, metric.name);
                    return {
                        ...metric,
                        displayName    : getTagMetricDisplayNameKey(metricKeyPrefix),
                        enumDefinitions: metric.enumDefinitions ? metric.enumDefinitions.map((enumDef) => {
                            const enumKeyPrefix = getEnumDefinitionKeyPrefix(metricKeyPrefix, enumDef.value);
                            return {
                                ...enumDef,
                                name: getEnumDefinitionNameKey(enumKeyPrefix),
                            };
                        }) : undefined,
                    };
                }),
            };
        });
    }
    return undefined;
};

const replaceEntityClassDisplaySettingsWithTranslationKeys = (
    keyPrefix: string,
    displaySettings: EntityClass['displaySettings'],
): EntityClass['displaySettings'] => {
    return {
        ...displaySettings,
        name       : getEntityClassNameKey(keyPrefix),
        description: getEntityClassDescriptionKey(keyPrefix),
    };
};

const localizeEntityClass = (entityClassName: string, entityClass: EntityClass): Partial<EntityClass> => {
    const keyPrefix = getEntityClassKeyPrefix(entityClassName);

    const localizedEntityClass: any = {};
    Object.keys(entityClass).forEach((key) => {
        if (key === 'entityClass') {
            localizedEntityClass.entityClass = entityClass.entityClass;
        }
        if (key === 'blobDefinitions') {
            localizedEntityClass.blobDefinitions = entityClass.blobDefinitions;
        }
        if (key === 'displaySettings') {
            localizedEntityClass.displaySettings = replaceEntityClassDisplaySettingsWithTranslationKeys(keyPrefix, entityClass.displaySettings);
        }
        if (key === 'attributeSchema') {
            localizedEntityClass.attributeSchema = replaceEntityClassAttributeSchemaAttributeValuesWithTranslationKeys(
                keyPrefix, entityClass.attributeSchema,
            );
        }
        if (key === 'metricsDefinitions') {
            localizedEntityClass.metricsDefinitions = replaceEntityClassMetricsDefinitionsAttributeValuesWithTranslationKeys(
                keyPrefix, entityClass.metricsDefinitions,
            );
        }
    });
    return localizedEntityClass;
};


export const EntityClassPreviewContainer = (props: EntityClassPreviewContainerProps) => {
    const classes = useStyle();
    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);

    const selectedEntityClass = props.entityClass;
    const [isEditing, setIsEditing] = useState<boolean>();
    const [inputError, setInputError] = useState<string | undefined>();
    const isNewEntityClass = selectedEntityClass === undefined;


    /**
     * Unselect the selected entity class and update the URL
     */
    const unselectEntityClass = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputError(undefined);

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (isNewEntityClass) {
            unselectEntityClass();
        }
    }, [closeEditing, isNewEntityClass, unselectEntityClass]);


    /**
     * On component load, if there is no selected entity classes, we can assume the user wants to create a new entity class therefore
     * set to edit mode right away
     */
    useEffect(() => {
        if (!selectedEntityClass) {
            onChangeEditingMode(true);
        }
    }, [onChangeEditingMode, selectedEntityClass]);


    const entityClassInfoSettings = useMemo((): EntityClassInfoProps['fields'] => {
        const entityClassJSON = selectedEntityClass ? {
            attributeSchema   : selectedEntityClass.attributeSchema,
            blobDefinitions   : selectedEntityClass.blobDefinitions,
            displaySettings   : selectedEntityClass.displaySettings,
            metricsDefinitions: selectedEntityClass.metricsDefinitions,
        } : {
            entityClass    : '{{ENTITY_CLASS_NAME_HERE}}',
            displaySettings: {
                name       : '{{ENTITY_NAME_HERE}}',
                description: '{{ENTITY_DESCRIPTION_HERE}}',
            },
        };

        return {
            entityClassJSON: {
                label   : 'Entity Class Definition',
                editable: true,
                value   : JSON.stringify(entityClassJSON, undefined, 4),
                error   : inputError || '',
            },
            createdAt: {
                label     : 'Created Date',
                value     : selectedEntityClass?.createdAt ? getDateTimeDisplay(selectedEntityClass.createdAt, trans) : undefined,
                hidden    : isNewEntityClass,
                hideOnEdit: true,
            },
            updatedAt: {
                label     : 'Updated Date',
                value     : selectedEntityClass?.updatedAt ? getDateTimeDisplay(selectedEntityClass.updatedAt, trans) : undefined,
                hidden    : isNewEntityClass,
                hideOnEdit: true,
            },
        };
    }, [selectedEntityClass, inputError, trans, isNewEntityClass]);


    /**
     * Validate the value string and reformat the JSON
     */
    const onFormatJSON = useCallback((
        value: string,
        onCompleted: (newValue: string)=> void,
        onFailure: (errorMessage: string)=> void,
    ) => {
        try {
            onCompleted(JSON.stringify(JSON.parse(value), undefined, 4));
        } catch (error) {
            uiDispatch(showErrorNotification((error as Error).message));
            onFailure((error as Error).message);
        }
    }, [uiDispatch]);


    /**
     * Set up the entity class for localization
     */
    const onSetupEntityClassForTranslation = useCallback((
        value: string,
        onCompleted: (newValue: string | undefined)=> void,
        onFailure: (newValue: string | undefined)=> void,
    ) => {
        // This feature should only be used for EDITING entity class only
        if (!selectedEntityClass) {
            return;
        }

        try {
            const entityClassObject = JSON.parse(value);
            uiDispatch(showDialog({
                type   : DialogType.WARNING,
                title  : 'Set up entity class for localization',
                message: `This will replace all string values in the entity class with translation keys. Are you sure you want to continue?
                            Please make sure you do "Generate Project Translation JSON" first before doing this.`,
                negativeButton: 'No',
                positiveButton: 'yes',
                onClose       : (response) => {
                    if (response?.code === DialogResponseCode.POSITIVE) {
                        const result = localizeEntityClass(selectedEntityClass.entityClass, entityClassObject);
                        onCompleted(JSON.stringify(result, undefined, 4));
                    }
                },
            }));
        } catch (error) {
            uiDispatch(showErrorNotification((error as Error).message));
            onFailure((error as Error).message);
        }
    }, [selectedEntityClass, uiDispatch]);


    const doDeleteEntityClass = useCallback(async (
        entityClass: string,
        errorMessage?: string,
    ) => {
        // Confirm with the user before updating the entity class
        uiDispatch(showDialog({
            type          : DialogType.INPUT,
            title         : 'Delete Entity Class',
            message       : `[WARNING] Are you sure you want to DELETE the entity class "${entityClass}"? Please confirm by entering the entity class name you want to delete`,
            negativeButton: trans('label.cancel'),
            positiveButton: trans('label.delete'),
            input         : {
                name    : 'Entity Class',
                type    : 'text',
                error   : errorMessage,
                required: true,
            },
            onClose: async (response) => {
                if (response?.code === DialogResponseCode.POSITIVE) {
                    if (response.input === entityClass) {
                        try {
                            await appDataDispatch(deleteEntityClass(props.projectId, entityClass));
                            closeEditing();
                            unselectEntityClass();
                            uiDispatch(showSuccessNotification(`Entity class '${entityClass}' deleted successfully`));
                        } catch (error) {
                            uiDispatch(showErrorNotification((error as ApiError).code));
                        }
                    } else if (!response.input || response.input.trim().length === 0) {
                        doDeleteEntityClass(entityClass, 'Please enter the entity class\' class name');
                    } else {
                        doDeleteEntityClass(entityClass, 'The entity class you enter does not match');
                    }
                }
            },
        }));
    }, [appDataDispatch, closeEditing, props.projectId, trans, uiDispatch, unselectEntityClass]);


    const doUpdateEntityClass = useCallback(async (
        entityClass: string,
        entityClassJSON: string | undefined,
    ) => {
        try {
            if (!entityClassJSON || entityClassJSON.length === 0) {
                throw new Error('Entity class definition is required');
            }

            const entityClassObject = JSON.parse(entityClassJSON);
            if (!isObjectLiteral(entityClassObject)) {
                throw new Error('Invalid JSON');
            }

            // Confirm with the user before updating the entity class
            uiDispatch(showDialog({
                type          : DialogType.CONFIRM,
                title         : 'Update Entity Class',
                message       : `Are you sure you want to update the entity class "${entityClass}"?`,
                negativeButton: trans('label.cancel'),
                positiveButton: trans('label.yes'),
                onClose       : async (response) => {
                    if (response?.code === DialogResponseCode.POSITIVE) {
                        try {
                            await appDataDispatch(updateEntityClass(props.projectId, entityClass, entityClassObject));
                            closeEditing();
                            unselectEntityClass();
                            uiDispatch(showSuccessNotification(`Entity class '${entityClass}' updated successfully`));
                        } catch (error) {
                            uiDispatch(showErrorNotification((error as ApiError).code));
                        }
                    }
                },
            }));
        } catch (error) {
            uiDispatch(showErrorNotification((error as Error).message));
        }
    }, [appDataDispatch, closeEditing, props.projectId, trans, uiDispatch, unselectEntityClass]);


    const doCreateEntityClass = useCallback(async (
        entityClassJSON: string | undefined,
    ) => {
        try {
            if (!entityClassJSON || entityClassJSON.length === 0) {
                throw new Error('Entity class definition is required');
            }

            const entityClassObject = JSON.parse(entityClassJSON);
            if (!isObjectLiteral(entityClassObject)) {
                throw new Error('Invalid JSON');
            }

            await appDataDispatch(createEntityClass(props.projectId, entityClassObject));
            closeEditing();
            unselectEntityClass();
            uiDispatch(showSuccessNotification('Entity class created successfully'));
        } catch (error) {
            uiDispatch(showErrorNotification((error as Error).message));
        }
    }, [appDataDispatch, closeEditing, props.projectId, uiDispatch, unselectEntityClass]);



    const onFormSubmit = useCallback((inputs: {
        readonly entityClassJSON: string | undefined
    }) => {
        if (!inputs.entityClassJSON || inputs.entityClassJSON.trim().length === 0) {
            setInputError('Value is required');
        } else if (selectedEntityClass) {
            doUpdateEntityClass(selectedEntityClass.entityClass, inputs.entityClassJSON);
        } else {
            doCreateEntityClass(inputs.entityClassJSON);
        }
    }, [selectedEntityClass, doUpdateEntityClass, doCreateEntityClass]);


    const slideOutCompContent = useMemo(() => {
        return (
            <EntityClassInfo
                className={clsx(classes.entityClassPanel, isEditing && 'editing')}
                title={isNewEntityClass ? 'New Entity Class Definition' : `Entity Class Definition for '${selectedEntityClass?.entityClass}'`}
                icon="router"
                fields={entityClassInfoSettings}
                isEditing={isEditing}
                actions={{
                    setUpEntityClassForTranslation: {
                        label   : 'Set Up Entity Class For Localization',
                        variant : 'contained',
                        color   : 'primary',
                        disabled: isNewEntityClass,
                        onClick : onSetupEntityClassForTranslation,
                    },
                    formatJson: {
                        label  : capitalizeAll(trans('label.format_json')),
                        variant: 'contained',
                        color  : 'primary',
                        onClick: onFormatJSON,
                    },
                    startEdit: {
                        onClick: () => {
                            onChangeEditingMode(true);
                        },
                    },
                    cancelEdit: {
                        label  : capitalizeAll(trans('label.cancel')),
                        onClick: onCancelEdit,
                    },
                    saveUpdate: {
                        label  : isNewEntityClass ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                        onClick: onFormSubmit,
                    },
                }}
                onFieldClicked={() => {
                    onChangeEditingMode(true);
                }}
                onFormInputChange={() => {
                    setInputError(undefined);
                }}
            />
        );

    }, [classes.entityClassPanel, isEditing, isNewEntityClass, selectedEntityClass?.entityClass, entityClassInfoSettings, onSetupEntityClassForTranslation,
        trans, onFormatJSON, onCancelEdit, onFormSubmit, onChangeEditingMode]);


    /**
     * Create and return a list of secondary actions for component
     */
    const secondaryActions = useMemo((): SlideOutCompProps['secondaryActions'] => {
        if (selectedEntityClass) {
            return [{
                label    : capitalizeAll(trans('label.delete_object', { term: trans('terms.entity_class') })),
                icon     : 'delete',
                className: clsx(classes.secondaryAction, 'delete'),
                onClick  : () => {
                    doDeleteEntityClass(selectedEntityClass.entityClass);
                },
            }];
        }
        return undefined;
    }, [classes.secondaryAction, doDeleteEntityClass, selectedEntityClass, trans]);

    return (
        <SlideOutComp
            className={classes.root}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            hideActionBar={isEditing}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
